import React from "react";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Building2, ClipboardCheck, Coffee, Droplet, Sparkles, Users, TrendingUp, Plus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatSkeleton } from "./Skeletons";

export default function OverviewTab({ onGoTo }) {
  const { isRTL } = useI18n();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["mycafe-overview"], queryFn: endpoints.myCafeOverview,
  });

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="mycafe-overview-loading">
        <StatSkeleton count={4} />
        <StatSkeleton count={3} />
      </div>
    );
  }

  const c = data?.counts || {};
  const avg = data?.avg_score || 0;
  const month = data?.month_visits || 0;
  const recent = data?.recent_visits || [];
  const branches = data?.branches || [];
  const scoreColor = avg >= 80 ? "text-emerald-600" : avg >= 60 ? "text-amber-500" : avg > 0 ? "text-red-500" : "text-[#5C544F]";

  return (
    <div className="space-y-8" data-testid="mycafe-overview">
      {/* Greeting + primary CTAs */}
      <div className="soft-card rounded-2xl p-6 lg:p-8 bg-gradient-to-br from-[#2A2421] to-[#3a3128] text-[#F9F6F0]">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-2">
              {isRTL ? "أهلاً بعودتك" : "Welcome back"}
            </div>
            <h2 className={`${isRTL ? "font-serif-ar" : "font-serif-en"} text-3xl lg:text-4xl leading-tight`}>
              {user?.name || (isRTL ? "صديقي" : "Friend")}
            </h2>
            <p className="text-sm text-[#F9F6F0]/70 mt-2 max-w-md">
              {isRTL
                ? "كل ما يهم مقهاك في مكان واحد — زيارات الجودة، الوصفات الخاصة، وفريقك."
                : "Everything that matters for your cafe in one private place — visits, recipes, and your team."}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => onGoTo("visits")} className="bg-[#C86240] hover:bg-[#B25535] text-white" data-testid="overview-cta-visit">
              <ClipboardCheck className="w-4 h-4 me-1.5" />
              {isRTL ? "زيارة جديدة" : "New visit"}
            </Button>
            <Button onClick={() => onGoTo("espresso")} variant="outline" className="border-[#F9F6F0]/30 text-[#F9F6F0] hover:bg-white/10 hover:text-[#F9F6F0]" data-testid="overview-cta-recipe">
              <Plus className="w-4 h-4 me-1.5" />
              {isRTL ? "وصفة" : "Recipe"}
            </Button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="overview-kpis">
        <Kpi icon={TrendingUp} label={isRTL ? "متوسط الجودة" : "Avg quality"} value={avg ? `${avg}` : "—"} valueClass={scoreColor} sub={avg ? "/100" : ""} />
        <Kpi icon={ClipboardCheck} label={isRTL ? "زيارات الشهر" : "This month"} value={month} sub={isRTL ? "زيارة" : "visits"} />
        <Kpi icon={Building2} label={isRTL ? "فروع" : "Branches"} value={c.branches || 0} />
        <Kpi icon={ClipboardCheck} label={isRTL ? "إجمالي الزيارات" : "Total visits"} value={c.visits || 0} />
        <Kpi icon={Coffee} label={isRTL ? "إسبريسو" : "Espresso"} value={c.espresso || 0} />
        <Kpi icon={Droplet} label={isRTL ? "قهوة اليوم" : "Drip"} value={c.drip || 0} />
        <Kpi icon={Users} label={isRTL ? "الفريق" : "Team"} value={c.team || 0} />
      </div>

      {/* Recent visits + Per-branch */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Section
          title={isRTL ? "آخر الزيارات" : "Recent visits"}
          actionLabel={isRTL ? "عرض الكل" : "View all"}
          onAction={() => onGoTo("visits")}
        >
          {recent.length === 0 ? (
            <EmptyHint icon={ClipboardCheck} text={isRTL ? "لم تسجّل أي زيارة بعد" : "No visits logged yet"} />
          ) : (
            <div className="space-y-2">
              {recent.map((v) => (
                <RecentVisitRow key={v.id} v={v} isRTL={isRTL} onClick={() => onGoTo("visits")} />
              ))}
            </div>
          )}
        </Section>

        <Section
          title={isRTL ? "أداء الفروع" : "Branch performance"}
          actionLabel={isRTL ? "إدارة" : "Manage"}
          onAction={() => onGoTo("branches")}
        >
          {branches.length === 0 ? (
            <EmptyHint icon={Building2} text={isRTL ? "لم تضف أي فرع بعد" : "No branches yet"} />
          ) : (
            <div className="space-y-2">
              {branches.slice(0, 5).map((b) => <BranchPerfRow key={b.id} b={b} isRTL={isRTL} />)}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, valueClass = "text-[#2A2421]", sub }) => (
  <div className="soft-card rounded-xl p-4">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#5C544F] mb-1.5">
      <Icon className="w-3 h-3 text-[#C86240]" /> {label}
    </div>
    <div className="flex items-baseline gap-1">
      <div className={`text-3xl font-bold ${valueClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#5C544F]">{sub}</div>}
    </div>
  </div>
);

const Section = ({ title, children, actionLabel, onAction }) => (
  <div className="soft-card rounded-2xl p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C86240]">{title}</div>
      {onAction && (
        <button onClick={onAction} className="text-xs text-[#5C544F] hover:text-[#C86240] inline-flex items-center gap-1">
          {actionLabel} <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
    {children}
  </div>
);

const EmptyHint = ({ icon: Icon, text }) => (
  <div className="py-8 text-center">
    <Icon className="w-8 h-8 mx-auto text-[#C86240]/50 mb-2" />
    <div className="text-sm text-[#5C544F]">{text}</div>
  </div>
);

const RecentVisitRow = ({ v, isRTL, onClick }) => {
  const score = v.overall_score || 0;
  const color = score >= 80 ? "text-emerald-600" : score >= 60 ? "text-amber-500" : "text-red-500";
  const dt = v.visit_date ? new Date(v.visit_date) : null;
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#F9F6F0] transition-colors text-start"
    >
      <div className={`text-2xl font-bold ${color} min-w-[48px] text-center`}>{score}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#2A2421] truncate">{v.branch_name || (isRTL ? "(بدون فرع)" : "(no branch)")}</div>
        <div className="text-xs text-[#5C544F]">{dt ? dt.toLocaleDateString(isRTL ? "ar-SA" : "en-GB") : ""}</div>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-[#5C544F]" />
    </button>
  );
};

const BranchPerfRow = ({ b, isRTL }) => {
  const s = b.avg_score;
  const color = s == null ? "text-[#5C544F]" : s >= 80 ? "text-emerald-600" : s >= 60 ? "text-amber-500" : "text-red-500";
  const last = b.last_visit_date ? new Date(b.last_visit_date) : null;
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F9F6F0]">
      <Building2 className="w-4 h-4 text-[#C86240]" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#2A2421] truncate">{b.name}</div>
        <div className="text-xs text-[#5C544F]">
          {b.visit_count || 0} {isRTL ? "زيارة" : "visits"}
          {last ? ` · ${isRTL ? "آخر" : "last"} ${last.toLocaleDateString(isRTL ? "ar-SA" : "en-GB")}` : ""}
        </div>
      </div>
      <div className={`text-lg font-bold ${color}`}>{s ?? "—"}</div>
    </div>
  );
};
